const _excluded = ["items", "groupedItems", "selectedItems", "itemToString", "isItemDisabled"],
  _excluded2 = ["getDropdownProps", "getSelectedItemProps"],
  _excluded3 = ["getToggleButtonProps", "isOpen", "getItemProps", "controlProps", "menuProps"],
  _excluded4 = ["selectedItem"],
  _excluded5 = ["selectedItem", "getItemProps"],
  _excluded6 = ["item", "isSelected"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { useCallback } from 'react';
import { useSelect, useMultipleSelection } from 'downshift';
import { useDropdown } from '.';
import { checkReferenceEquality, defaultIsItemDisabled, defaultLabelGetter, wrapGetSelectedItemProps } from '../helpers';
/**
 * A hook used to manage all logic needed for a multiple select Dropdown.
 */
const useMultiDropdown = _ref => {
  let {
      items,
      groupedItems,
      selectedItems: selectedItemsProp,
      itemToString = item => defaultLabelGetter(item),
      isItemDisabled = defaultIsItemDisabled
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const maybeGroupedItems = items || groupedItems || [];
  checkReferenceEquality(_objectSpread(_objectSpread({}, props), {}, {
    items: maybeGroupedItems,
    selectedItems: selectedItemsProp
  }));
  const useMultipleSelectionArgs = {};
  if (selectedItemsProp) {
    // Setting this to undefined results in "cannot get length of undefined", so do this hack
    useMultipleSelectionArgs.selectedItems = selectedItemsProp;
  }
  const _useMultipleSelection = useMultipleSelection(_objectSpread(_objectSpread({}, useMultipleSelectionArgs), {}, {
      // Setting initial active index blocks downshift from focusing input on mount
      initialActiveIndex: 0
    }, props)),
    {
      getDropdownProps,
      getSelectedItemProps: rawGetSelectedItemProps
    } = _useMultipleSelection,
    useMultipleSelectionReturnValue = _objectWithoutProperties(_useMultipleSelection, _excluded2);
  const stateReducer = (state, _ref2) => {
    let {
      changes,
      type
    } = _ref2;
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        return _objectSpread(_objectSpread({}, changes), {}, {
          isOpen: true,
          // keep menu open after selection
          highlightedIndex: state.highlightedIndex // maintain highlighted item after selection
        });
      default:
        return changes;
    }
  };
  const onStateChange = _ref3 => {
    let {
      type,
      selectedItem
    } = _ref3;
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        if (!selectedItem) {
          break;
        }
        if (!useMultipleSelectionReturnValue.selectedItems.includes(selectedItem)) {
          useMultipleSelectionReturnValue.addSelectedItem(selectedItem);
        } else {
          useMultipleSelectionReturnValue.removeSelectedItem(selectedItem);
        }
        break;
      default:
        break;
    }
  };

  // workaround, the implicit XOR type gets lost causing type errors.  Casting it here makes it explicit
  const itemArgs = {
    items,
    groupedItems
  };
  const _useDropdown = useDropdown(_objectSpread(_objectSpread({}, itemArgs), {}, {
      selectedItem: null,
      itemToString,
      stateReducer,
      onStateChange,
      isItemDisabled
    }, props)),
    {
      getToggleButtonProps: rawGetToggleButtonProps,
      isOpen,
      getItemProps: rawGetItemProps,
      controlProps: allControlProps,
      menuProps: allMenuProps
    } = _useDropdown,
    useDropdownReturnValue = _objectWithoutProperties(_useDropdown, _excluded3);
  const {
      selectedItem: _ignored1
    } = allControlProps,
    controlProps = _objectWithoutProperties(allControlProps, _excluded4);
  const {
      selectedItem: _ignored2,
      getItemProps: _ignoredGetItemProps
    } = allMenuProps,
    menuProps = _objectWithoutProperties(allMenuProps, _excluded5);
  const getToggleButtonProps = (options, extraOptions) => rawGetToggleButtonProps(_objectSpread({
    // downshift useSelect is for some reason sending a toggle button blur
    // event after the click event, when you click on the toggle button the
    // very first time a page loads found this somewhat related issue that seems to fix this:
    // https://github.com/downshift-js/downshift/issues/966
    onBlur: event => {
      event.preventDownshiftDefault = true;
    }
  }, getDropdownProps(_objectSpread({
    preventKeyAction: isOpen
  }, options), extraOptions)));
  const getItemProps = useCallback(_ref4 => {
    let {
        item,
        isSelected: isSelectedProp
      } = _ref4,
      itemProps = _objectWithoutProperties(_ref4, _excluded6);
    const isSelected = isSelectedProp || useMultipleSelectionReturnValue.selectedItems.includes(item);
    const toReturn = rawGetItemProps(_objectSpread({
      item,
      isSelected
    }, itemProps));
    return _objectSpread(_objectSpread({}, toReturn), {}, {
      isMultiple: true
    });
  }, [rawGetItemProps, useMultipleSelectionReturnValue.selectedItems]);
  const getSelectedItemProps = useCallback(args => wrapGetSelectedItemProps({
    getSelectedItemProps: rawGetSelectedItemProps,
    isItemDisabled
  })(args), [isItemDisabled, rawGetSelectedItemProps]);
  return _objectSpread(_objectSpread({
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    isOpen,
    /**
     * @deprecated use value returned from menuProps
     */
    getItemProps,
    /**
     * @deprecated use value returned from controlProps
     */
    getToggleButtonProps,
    getSelectedItemProps,
    controlProps: _objectSpread(_objectSpread({}, controlProps), {}, {
      getToggleButtonProps,
      selectedItems: useMultipleSelectionReturnValue.selectedItems,
      getSelectedItemProps,
      removeSelectedItem: useMultipleSelectionReturnValue.removeSelectedItem
    }),
    menuProps: _objectSpread(_objectSpread({}, menuProps), {}, {
      selectedItems: useMultipleSelectionReturnValue.selectedItems,
      addSelectedItem: useMultipleSelectionReturnValue.addSelectedItem,
      removeSelectedItem: useMultipleSelectionReturnValue.removeSelectedItem,
      getItemProps
    })
  }, useMultipleSelectionReturnValue), useDropdownReturnValue);
};
export default Object.assign(useMultiDropdown, {
  stateChangeTypes: useDropdown.stateChangeTypes,
  multipleSelectionStateChangeTypes: useMultipleSelection.stateChangeTypes
});
//# sourceMappingURL=useMultiDropdown.js.map